const Data = {
  info: {
    title: "C.002 Helena Hauff: The Rules Don't Apply",
    code: "C.002",
    artist: "Helena Hauff",
    subTitle: "The Rules Don't Apply",
    standFirst:
      "Behind the staying power of one of dance music's most mysterious— and most celebrated— DJs.",
    words: "Chloe Lula",
    images: "Stini Röhrs",
    leadImage: "lead.png",
    published: "15/05/2024",
    credits: [
      ["Words", "Chloe Lula"],
      ["Photographer / Director", "Stini Röhrs"],
      ["Producer", "Joelle Flacke"],
      ["Production Company", "WA GMBH"],
      ["DOP", "Anton Beliaev"],
      ["Styling", "Alexander Gabriel"],
      ["HMU Artist", "Janina Zais"],
      ["Hair Stylist", "Diego Fraile"],
      ["HMU Assistance", "Shayna Lewis Fenton"],
      ["Set Design", "Tamara Bruckner"],
      ["Lights / Digi Op", "Alex Craddock"],
      ["Photo Assistant", "Jakob Riemann"],
    ],
  },
  content: [
    {
      type: "StandFirst",
      text: "Behind the staying power of one of dance music's most mysterious— and most celebrated— DJs.",
    },
    {
      type: "Paragraph",
      text: `In place of a standard accolade-touting career biography, the top right corner of Helena Hauff's
            SoundCloud page reads: "Helena Hauff! Du rufst an, ich leg auf." The German phrase is a shrewd
            double entendre that makes use of auflegen's two meanings—"to DJ" and "to hang up." Call
            Helena and she'll play for you. Or, maybe more likely, she'll just put down the phone. When I asked
            her about it over a coffee one crisp afternoon in early September, she laughed and said she
            thought it was funny and rhymed with her stage name. But more than a joke, the shibboleth is
            reflective of an irreverent worldview that's led to the Hamburg DJ's intrigue and anomalous staying
            power after more than a decade of above-ground success: authentic and steadfast, she refuses to
            answer to trends or the industry's sometimes crippling pressures to conform.`,
    },
    {
      type: "Paragraph",
      text: `Now a flag bearer for the lo-fi electro leanings associated with Bunker Records and go-for-broke
            '90s warehouse raves, Hauff has become the ambassador for a suite of retro, underground sounds
            while still achieving commercial appeal. That she has been playing such large-scale events as
            Awakenings, Gottwood, Primavera and countless major clubs around the world for years is a feat
            amid the choices she's made to run counter to the mainstream. A vinyl-only DJ with no manager,
            press campaigns, social media account or even a smartphone, she has defied the unwritten laws
            that govern the modern music industry, which privilege artists who satisfy the demands of
            algorithmic populism and the Instagram economy. More than a decade into her career, Hauff—who
            is celebrating her third BBC Radio 1 residency and a release on London's fabric Originals this
            month—has competed with and even surpassed many of her contemporaries, remaining relevant
            in spite of, or even because of, her outsider approach to music for music's sake.`,
    },
    {
      type: "PullQuote",
      text: `"The core of the music has never changed. It's about that raw energy—it's what
            I've always been into."`,
      images: [
        ["RA_HH_06.png"],
        ["RA_HH_016.png"],
        ["RA_HH_018.png"],
        ["RA_HH_09.png"],
      ],
    },
    {
      type: "Paragraph",
      text: `Slight and lean with a tangle of brown hair, Hauff is charmingly self-effacing in discussing her
            explosive ascent. "I don't think that much about it," she told me, coolly rolling up the sleeves of a
            loose-fitting denim suit. "I don't have a plan. I never had a plan. How the hell I ended up getting the
            bookings I have now… I don't know how it happened."`,
    },
    {
      type: "Paragraph",
      text: `Though flippant, Hauff partially attributes her success to her abiding commitment to the tastes she
            honed as a university student in Hamburg. Bred on a musical diet of The Stooges and Loop, she
            was sonically primed for the strobe-lit intensity of DIY electronica, which she recalls first randomly
            picking up at a record store when she was 18. She quickly grew fond of Dutch artists like Guy
            Tavares and I-F, who have notably not changed their squelching school of acid and electro in the
            30 years since they first started throwing parties. It wasn't long before she started collecting similar
            records and DJing, and then holding a Tuesday night residency at the vaunted local haunt Golden
            Pudel, at which she invited Tavares and I-F to play.`,
    },
    {
      type: "Paragraph",
      text: `Golden Pudel's understated anarchist ideology resonated with Hauff as she began hosting her own
            nights and cultivating a hometown following. "I realised pretty quickly that the people surrounding
            the Pudel were pretty politically involved," she told the Guardian in 2018. "Not like a political party,
            but a kind of anti-capitalist idea."`,
    },
    {
      type: "Paragraph",
      text: `Hauff's affinity towards the club's punk-minded ethos blossomed in tandem with her escalating
            focus on production and record digging, which became an obsession to the point that she dropped
            out of school to pursue music full-time. At first her focus was on her studio. She used (and
            continues to work from) an all-hardware setup, whose immediacy and limitations allowed her to
            elicit the cantankerous sounds being circulated by some electro pioneers in The Hague. Some of
            her earliest releases from this period, like Return To Disorder, which became the name of her
            record label, are particularly raw. The tracks, which straddle electro and subversive hardcore,
            rarely peak and rarely resolve, deploying mincing synths and washes of static that chew up the ear.
            To her, the style looked towards the future while remaining rooted in a lineage of protest music that
            soundtracked Detroit's recent past.`,
    },
    {
      type: "Paragraph",
      text: `While Hauff excelled at production and continued to make music at a dizzying pace, it was her DJ
sets that propelled her to recognition not long into her budding career. She proved to be a
discerning selector as well as a highly technical mixer, and her chops behind the decks attracted
high-profile admirers like Actress—who released her debut album, Discreet Desires, on Ninja Tune
sub-label Werk Discs in 2015—when he saw her play at Pudel as a 20-year-old.`,
    },
    {
      type: "Carousel",
      images: [
        ["RA_HH_CAROUSEL_01.png"],
        ["RA_HH_CAROUSEL_02.png"],
        ["RA_HH_CAROUSEL_03.png"],
      ],
    },
    {
      type: "Paragraph",
      text: `"I still think she's the best DJ in the world," her close friend and collaborator L.F.T., an electro DJ
and producer from Hamburg, told me over the phone. "She knows exactly which track is on each
record—she has an almost photographic memory and knows what will fit into each other." Having
played back-to-back with her a handful of times, including a hugely popular 2020 set on Berlin's
HÖR, he said she exudes extreme calm behind the decks, and has an uncanny ability to mix any
two records together perfectly in less than five seconds. "She knows what will happen before it
happens," he said. "It's pretty special."`,
    },
    {
      type: "Paragraph",
      text: `I spoke to Hauff about how she honed her technique, inquiring specifically about whether or not
she wrote notes on her record sleeves, as many vinyl DJs do as cues for themselves in the club.
She said no. Instead, she has a special type of synesthesia that allows her to "see" songs. "Quite
often I play a record, and I don't know what the hell it is or what the artist is called, but I can see
the structure in my head," she said. The anatomy of each track resembles a complex "anti-
clockwise clock" where different parts of the song form structures that imprint themselves in her
mind's eye immediately upon first listen. "I can see exactly how many bars are in a track, when a
new element will start, where the hi-hats are," she added. As such, she doesn't require the screen
of a CDJ, instead pulling from an extensive collection of rhythmically and stylistically diverse vinyl
that she can methodically construct into seamless, genre-spanning sets.`,
    },
    {
      type: "Paragraph",
      text: `Hauff is impressive behind the turntables. She's commanding and magnetic, playing with a
casualness that belies the rigor of mixing high-BPM records for crowds of spiral-eyed partygoers in
claustrophobic clubs. (Though she told me that one turntable malfunction literally left her crying
under the DJ booth between mixes, she conceded that any slip-ups likely weren't perceived by
anyone but her). At Lyon festival Nuits Sonores in May, I watched from La Sucrière's overhead
mezzanine as she lissomely popped records onto the turntables, pausing to leisurely hand roll
cigarettes and scan through her record bag while still engaging with a spellbound throng of more
than 2,000 people. The performance was exceptional to say the least, securing my full and
undivided attention in a way that few DJs still can.`,
    },
    {
      type: "Paragraph",
      text: `But it was at Hauff's much-anticipated Berghain appearance in September that she flaunted the full
range of her skills, showcasing how her curation, even more than her technical panache, has
secured her enduring popularity. In a venue whose bread and butter is the knuckle-dragging
routine of four-to-the-floor techno, her set was a refreshing landscape of dynamic peaks and
valleys. Troweling from a timeline of noisy, neo-gothic dance music—curdling acid, full-pelt
industrial, fanged electro—her selection harkened at times to gritty '90s nostalgia and at others to
mechanical futurism. It also eschewed a set's traditional shape in which tensions climax and build.
Her method was to churn up themes, cycling fluidly between different strains of off-kilter dance
music played one after another. While this tactic could have come across as eclectic at best (or
abrupt at worst) in lesser hands, in Hauff's, the result was to create less of a trajectory than an
overall atmosphere.`,
    },
    {
      type: "Paragraph",
      text: `"It creates a lot more energy [to play different genres]," she told me after the set. "It's also a lot
harder than playing just one thing. But when it goes well, it creates this uniform sound over time.
And if you listen to that for three or four hours, then you can hear that it will make sense together—
you'll see the big picture."`,
    },
    {
      type: "Carousel",
      images: [
        ["RA_HH_CAROUSEL_04.png"],
        ["RA_HH_CAROUSEL_05.png"],
        ["RA_HH_CAROUSEL_06.png"],
      ],
    },
    {
      type: "Paragraph",
      text: `Hauff uses this kind of strategic titration to gradually introduce niche genres to festival audiences
that might not be as attuned to underground dance music, playing one challenging track deep into
a mix after an hour of supple build-up. It's how she's managed to bridge such disparate scenes and
hold onto a broad listenership, accumulating followers who both enjoy the shapeshifting backdrop
of her dark selections and the effortlessness with which she introduces styles that would otherwise
feel out of context or beyond the norm. A friend of mine who has historically favoured
deconstructed club music and Hessle Audio-like bass summarized Hauff's idiosyncratic, cross-
genre allure well: "I usually hate electro," she said. "But I love Helena Hauff."`,
    },
    {
      type: "Paragraph",
      text: `The artist's new EP on fabric Originals is a testament to her unique ability to bridge scenes as a
highly diverse, yet still aesthetically consistent, selector. When I asked the London label about their
decision to work with her, they interestingly pointed to her DJ sets rather than her past
releases."Helena was one of the first names on our list of artists we wanted to work with," Jorge
Nieto, fabric's creative director, wrote over email. "She’s an excellent artist, always digging for new
tracks, and her sets are always refreshing. She has a passion for records and the craft that we
really love at the label."`,
    },
    {
      type: "Paragraph",
      text: `Out now, Living With Ladybirds sits well on fabric Originals, which seems to highlight artists who
are the torchbearers of their respective genres. The four tracks are true to her past work's spare
and unarmoured grit. "Touching Plastic" offsets the electric thrum of hardware with scuzzy drums
and a lone synthesizer, while "Your Turn To Fly" and the melodically comparable "Jonas" cull
heavily from the sketchy single-take feel of her 2015 LP, A Tape. Despite the arc of Hauff's career
since her last consistent string of releases before the pandemic, the overall impression is that she
has remained resolutely committed to a prototypical sound—one she has unwittingly become the
representative over the last few years.`,
    },
    {
      type: "Paragraph",
      text: `Part of Hauff's drawing power to a relatively above-ground audience is the distinct musical
sensibility she's disseminated in her studio and behind the DJ booth. But it's also the subtle ways
in which she courts intrigue by dodging conventions. Not only did she not approach fabric for Living
With Ladybirds—they tapped her for songs last January, and have seemingly been the only
purveyors of any kind of promotion—but she's allegedly never had any set ambitions for her music
more generally. Opportunities have emerged in the wake of her exceptional talent.`,
    },
    {
      type: "Paragraph",
      text: `"I just go along with what's being presented to me," she said at our second meeting. "I never
thought, 'What am I going to do next?' I know there are a lot of artists who have some kind of
business plan, but I just never have. I've never wanted to 'reach goals.'" The statement was less of
a put-down than an admission of punk naivete wherein her attention has been purely on the
records and releases that interest her and nothing else.`,
    },
    {
      type: "Paragraph",
      text: `In 2022, this audacity is rare and refreshing. The entrenched and extended power of social media
has fed the majority of the arts—and nightlife as a microcosm of these wider commercial
economies—into the maw of unfettered platform capitalism; understandably, many DJs and
producers have been thrust towards the kind of shameless self-promotion and easily-digestible
music-making that generates income by gratifying hungry Instagram scrollers and the companies
that peddle products to them. What's more, many artists used these networks to sustain wavering
careers at the height of the pandemic. Wanting to stay connected to their fans and keep the
attention of a dwindling (and increasingly competitive) cabal of clubs, they posted selfies and
produced accessible tunes that could proliferate on algorithms and discovery pages. Some artists
have been subject to criticism for ostensibly championing the love-and-acceptance values around
club music while buckling to pursue commercial profit. There are plenty of artists who have beefed
up their feeds and pivoted their signature styles in accordance with bank-backed trends.`,
    },
    {
      type: "PullQuote",
      text: `"No one can say that I've achieved what I have because of the nice pictures I
posted on Instagram. I'm just good at what I do.”`,
      images: [
        ["RA_HH_07.png"],
        ["RA_HH_02.png"],
        ["RA_HH_05.png"],
        ["RA_HH_010.png"],
      ],
    },
    {
      type: "Paragraph",
      text: `Hauff has a fan page that she claims not to be involved in, but she otherwise has no online
presence aside from her accumulated collection of interviews and reviews. There was a time at the
nadir of the 2020 lockdown when she considered joining a couple of platforms, but she's ultimately
happy she didn't, calling social media a "disease" that breeds mindless content and skewed
metrics for success. She's also aware of how her abstention has influenced the course of her
career."I think I've been a bit lucky that I haven't had to play the game" she reflected. "I think it's
gotten me more respect, because people notice that I'm not playing. I don't blame people for using
it as a tool, but at least now no one can say that I've achieved what I have because of the nice
pictures I posted on Instagram. I'm just good at what I do.”`,
    },
    {
      type: "Paragraph",
      text: `At a panel on business techno and the Instagram economy last summer, I was asked about the
intractable relationship some artists have with the DIY ethos if they want to get paid while staying
true to the underground. Is it possible to circulate through major venues while playing music from
lesser-known artists and garnering street cred? Hauff was the only artist who came to my mind as
someone who has been able to keep a foot in both worlds. She's traversed the rigid boundaries
that separate seedy squats and wide-acre festival circuits and continued to transfuse her vision
into both.`,
    },
    {
      type: "Paragraph",
      text: `On the ebbing crest of a release announcement and a busy summer tour, now would be a natural
time for Hauff to be anticipating her next EP, plotting her next move. Most artists would be filled
with a quaking anxiety at such an unfixed horizon. Instead, she's perpetually cool. "I don't know
what I'll be doing," she laughed.`,
    },
    {
      type: "Paragraph",
      text: `As with her DJing, Hauff has navigated her career by being impromptu and intuitive, letting the
pieces fall into place. In the time since we spoke, Hauff started a month-long residency with BBC
Radio 1. Her first hour-long mix is a high-octane trip through the recesses of her record collection,
exploring hiss-laden industrial and electro. In between songs, she narrates her selections, detailing
the memories that inspired some of her choices. The majority are timeless cuts she found when
she was 18 that she's still playing now. "I randomly bought this in the mid-2000s," she wrote to me
after the fact, citing Radioactive Man's "Uranium." "I remember thinking, 'This is the best thing I've
ever heard in my life.' The core of the music has never changed. It's about that raw energy—it's
what I've always been into."`,
    },
  ],
};

export default Data;
